// Sample Characters List Component
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Icon,
  Card,
  Loader,
  Header,
  Segment,
  Grid,
  Pagination,
  Dropdown,
  Divider,
  Table,
  Label,
  Popup
} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import voiceActressDefault from '../../../../assets/voiceActress.png';
import useSignedAvatar from '../../../../hooks/useSignedAvatar';
// import '../../../../styles/landing-pages.css';
import '../../../../styles/sample-voice-pages.css';

// Character Avatar Component with S3 URL signing
const CharacterAvatar = ({ avatarUrl, characterName, userId }) => {
  const { signedUrl } = useSignedAvatar(avatarUrl, userId, voiceActressDefault);

  return (
    <img
      src={signedUrl}
      alt={characterName || 'Character Avatar'}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      }}
      onError={(e) => {
        e.target.src = voiceActressDefault;
      }}
    />
  );
};

const SampleCharactersList = ({
  characters = [],
  loading,
  onSelectCharacter,
  onDeleteCharacter,
  onNewCharacter,
  selectedCharacter
}) => {
  // Get userId from Redux store for S3 URL signing - same path as SampleCharacterFormRefactored
  const userProfile = useSelector((state) => state.user?.profile);
  const userId = userProfile?.data?.userId || userProfile?.user?.uuid || null;

  // Log if userId is missing for debugging
  useEffect(() => {
    if (!userId) {
      console.warn('[SampleCharactersList] No userId found in Redux state. Avatar URLs may not be signed.');
    }
  }, [userId]);

  // Pagination state
  const [activePage, setActivePage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    parseInt(localStorage.getItem('sampleCharactersPerPage')) || 9
  );

  // View mode state (cards or table)
  const [viewMode, setViewMode] = useState(
    localStorage.getItem('sampleCharactersViewMode') || 'cards'
  );

  // Sorting state
  const [sortBy, setSortBy] = useState(
    localStorage.getItem('sampleCharactersSortBy') || 'name-asc'
  );

  // Sort options
  const sortOptions = [
    { key: 'name-asc', value: 'name-asc', text: 'Name (A-Z)', icon: 'sort alphabet down' },
    { key: 'name-desc', value: 'name-desc', text: 'Name (Z-A)', icon: 'sort alphabet up' },
    { key: 'type-asc', value: 'type-asc', text: 'Character Type', icon: 'users' },
    { key: 'genre-asc', value: 'genre-asc', text: 'Genre', icon: 'book' },
    { key: 'audio-desc', value: 'audio-desc', text: 'Has Audio First', icon: 'microphone' },
    { key: 'audio-asc', value: 'audio-asc', text: 'No Audio First', icon: 'microphone slash' },
    { key: 'status-asc', value: 'status-asc', text: 'Status (Active First)', icon: 'check circle' }
  ];

  // Items per page options
  const itemsPerPageOptions = [
    { key: 9, value: 9, text: '9 per page' },
    { key: 18, value: 18, text: '18 per page' },
    { key: 27, value: 27, text: '27 per page' },
    { key: 'all', value: 999, text: 'Show all' }
  ];

  // Sort characters
  const sortedCharacters = useMemo(() => {
    const sorted = [...characters].sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name-desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'type-asc':
          return (a.characterType || '').localeCompare(b.characterType || '');
        case 'genre-asc':
          return (a.genre || '').localeCompare(b.genre || '');
        case 'audio-desc':
          // Has audio first
          if (a.hasAudio && !b.hasAudio) return -1;
          if (!a.hasAudio && b.hasAudio) return 1;
          return 0;
        case 'audio-asc':
          // No audio first
          if (!a.hasAudio && b.hasAudio) return -1;
          if (a.hasAudio && !b.hasAudio) return 1;
          return 0;
        case 'status-asc':
          // Active first
          const aStatus = a.status || 'active';
          const bStatus = b.status || 'active';
          if (aStatus === 'active' && bStatus !== 'active') return -1;
          if (aStatus !== 'active' && bStatus === 'active') return 1;
          return 0;
        default:
          return 0;
      }
    });
    return sorted;
  }, [characters, sortBy]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedCharacters.length / itemsPerPage);
  const currentCharacters = useMemo(() => {
    const start = (activePage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return sortedCharacters.slice(start, end);
  }, [sortedCharacters, activePage, itemsPerPage]);

  // Reset to page 1 when sort changes or characters change
  useEffect(() => {
    setActivePage(1);
  }, [sortBy, characters.length]);

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('sampleCharactersSortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('sampleCharactersPerPage', itemsPerPage.toString());
  }, [itemsPerPage]);

  useEffect(() => {
    localStorage.setItem('sampleCharactersViewMode', viewMode);
  }, [viewMode]);

  // Handle page change
  const handlePageChange = (e, { activePage }) => {
    setActivePage(activePage);
  };

  // Handle sort change
  const handleSortChange = (e, { value }) => {
    setSortBy(value);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e, { value }) => {
    setItemsPerPage(value);
    setActivePage(1); // Reset to first page
  };
  if (loading) {
    return (
      <div className="loading-container">
        <Loader active inline="centered">
          Loading sample characters...
        </Loader>
      </div>
    );
  }

  return (
    <div className="sample-characters-list">
      <div className="characters-header">
        <Header as="h2" className='sectionTitleLight'>
          <Icon name="users" />
          Sample Characters
        </Header>
        <Button
          primary
          icon
          labelPosition="left"
          onClick={onNewCharacter}
        >
          <Icon name="plus" />
          New Sample Character
        </Button>
      </div>

      {characters.length === 0 ? (
        <Segment placeholder>
          <Header icon>
            <Icon name="user outline" />
            No Sample Characters Yet
          </Header>
          <p>Create your first sample character to showcase your voice talent.</p>
          <Button primary onClick={onNewCharacter}>
            <Icon name="plus" />
            Create Sample Character
          </Button>
        </Segment>
      ) : (
        <>
          {/* Controls Section */}
          <Segment basic style={{ paddingBottom: '0' }}>
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  {/* Showing count */}
                  <div style={{ paddingTop: '8px', fontSize: '1.1em', color: '#666' }}>
                    {sortedCharacters.length > 0 ? (
                      <>
                        Showing {Math.min((activePage - 1) * itemsPerPage + 1, sortedCharacters.length)}-
                        {Math.min(activePage * itemsPerPage, sortedCharacters.length)} of {sortedCharacters.length} characters
                      </>
                    ) : (
                      'No characters to display'
                    )}
                  </div>
                </Grid.Column>
                <Grid.Column width={8} textAlign="right">
                  <div style={{ display: 'inline-flex', gap: '10px', alignItems: 'center' }}>
                    {/* View mode toggle */}
                    <Button.Group size="small">
                      <Popup content="Card view" trigger={
                        <Button
                          icon="grid layout"
                          active={viewMode === 'cards'}
                          onClick={() => setViewMode('cards')}
                        />
                      } />
                      <Popup content="Table view" trigger={
                        <Button
                          icon="table"
                          active={viewMode === 'table'}
                          onClick={() => setViewMode('table')}
                        />
                      } />
                    </Button.Group>
                    {/* Items per page selector */}
                    <Dropdown
                      selection
                      compact
                      value={itemsPerPage}
                      options={itemsPerPageOptions}
                      onChange={handleItemsPerPageChange}
                      style={{ minWidth: '120px' }}
                    />
                    {/* Sort dropdown */}
                    <Dropdown
                      selection
                      value={sortBy}
                      options={sortOptions}
                      onChange={handleSortChange}
                      style={{ minWidth: '200px' }}
                      placeholder="Sort by..."
                    />
                  </div>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Segment>

          <Divider />

          {/* Conditional: Table View or Card Grid */}
          {viewMode === 'table' ? (
            <Table celled selectable striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={{ width: '50px' }}></Table.HeaderCell>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Type</Table.HeaderCell>
                  <Table.HeaderCell>Gender</Table.HeaderCell>
                  <Table.HeaderCell>Genre</Table.HeaderCell>
                  <Table.HeaderCell>Audio</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '110px' }}>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {currentCharacters.map((character) => (
                  <Table.Row
                    key={character.uuid}
                    active={selectedCharacter?.uuid === character.uuid}
                    onClick={() => onSelectCharacter(character)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Table.Cell>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #ddd'
                      }}>
                        <CharacterAvatar
                          avatarUrl={character.avatarUrl}
                          characterName={character.name}
                          userId={userId}
                        />
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{character.name || 'Unnamed'}</strong>
                      {character.role && (
                        <div style={{ color: '#888', fontSize: '0.85em' }}>
                          {character.role}
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>{character.characterType || '—'}</Table.Cell>
                    <Table.Cell>{character.gender || '—'}</Table.Cell>
                    <Table.Cell>{character.genre || '—'}</Table.Cell>
                    <Table.Cell>
                      {character.hasAudio ? (
                        <Label size="small" color="green" basic>
                          <Icon name="microphone" /> {character.variationCount || 0}
                        </Label>
                      ) : (
                        <Label size="small" basic>
                          <Icon name="microphone slash" /> None
                        </Label>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Label size="small" color={character.status === 'active' || !character.status ? 'green' : 'grey'}>
                        {character.status || 'active'}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Button.Group size="mini">
                        <Popup content="Edit character" trigger={
                          <Button
                            icon="edit"
                            color="blue"
                            onClick={(e) => { e.stopPropagation(); onSelectCharacter(character); }}
                          />
                        } />
                        <Popup content="Delete character" trigger={
                          <Button
                            icon="trash"
                            color="red"
                            onClick={(e) => { e.stopPropagation(); onDeleteCharacter(character); }}
                          />
                        } />
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            /* Characters Card Grid */
            <div className="characters-grid">
              {currentCharacters.map((character) => (
                <Card
                  key={character.uuid}
                  className={`character-card ${selectedCharacter?.uuid === character.uuid ? 'selected' : ''}`}
                  onClick={() => onSelectCharacter(character)}
                >
                  <Card.Content>
                    <div className="character-card-header" style={{ position: 'relative', minHeight: '20px' }}>
                      <div style={{
                        position: 'absolute',
                        top: '-10px',
                        right: '-10px',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        backgroundColor: '#f0f0f0',
                        border: '2px solid #ddd',
                        zIndex: 1,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <CharacterAvatar
                          avatarUrl={character.avatarUrl}
                          characterName={character.name}
                          userId={userId}
                        />
                      </div>
                      <div className="character-actions" style={{ float: 'right' }}>
                        <Button className="character-action-button edit-button" icon="edit" color="blue" size="mini" circular
                          onClick={(e) => { e.stopPropagation(); onSelectCharacter(character); }} title="Edit character" />
                        <Button className="character-action-button delete-button" icon="trash" color="red" size="mini" circular
                          onClick={(e) => { e.stopPropagation(); onDeleteCharacter(character); }} title="Delete character" style={{ marginLeft: '5px' }} />
                      </div>
                    </div>
                    <Grid>
                      <Grid.Row columns={1} style={{ paddingBottom: '0.5rem' }}>
                        <Grid.Column>
                          <div className="character-name" style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                            {character.name || 'Unnamed Character'}
                          </div>
                        </Grid.Column>
                      </Grid.Row>
                      {character.role && (
                        <Grid.Row columns={1} style={{ paddingTop: '0', paddingBottom: '0.5rem' }}>
                          <Grid.Column>
                            <div style={{ color: '#666' }}><Icon name="tag" />{character.role}</div>
                          </Grid.Column>
                        </Grid.Row>
                      )}
                      <Grid.Row columns={2} style={{ paddingTop: '0', paddingBottom: '0.5rem' }}>
                        <Grid.Column>
                          {character.characterType && (<div className="character-type"><strong>Type:</strong> {character.characterType}</div>)}
                        </Grid.Column>
                        <Grid.Column>
                          {character.genre && (<div className="character-genre"><strong>Genre:</strong> {character.genre}</div>)}
                        </Grid.Column>
                      </Grid.Row>
                      {character.description && (
                        <Grid.Row columns={1} style={{ paddingTop: '0' }}>
                          <Grid.Column>
                            <div className="character-description">
                              {character.description.length > 150 ? `${character.description.substring(0, 150)}...` : character.description}
                            </div>
                          </Grid.Column>
                        </Grid.Row>
                      )}
                    </Grid>
                    <Card.Meta style={{ marginTop: '1rem' }}>
                      <div className={`character-audio-status ${character.hasAudio ? 'has-audio' : ''}`}>
                        <Icon name={character.hasAudio ? 'microphone' : 'microphone slash'} />
                        {character.hasAudio ? (<span>{character.variationCount || 0} recording(s)</span>) : (<span>No recordings yet</span>)}
                      </div>
                    </Card.Meta>
                    <Card.Meta>
                      <span style={{ fontSize: '0.85em', color: '#999' }}>
                        {character.gender && `${character.gender} • `}
                        {character.age && `${character.age} • `}
                        Status: {character.status || 'active'}
                      </span>
                    </Card.Meta>
                  </Card.Content>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination Bar */}
          {totalPages > 1 && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <Pagination
                activePage={activePage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                ellipsisItem={{ content: '...', icon: 'ellipsis horizontal' }}
                firstItem={{ content: '«', icon: 'angle double left' }}
                lastItem={{ content: '»', icon: 'angle double right' }}
                prevItem={{ content: '‹', icon: 'angle left' }}
                nextItem={{ content: '›', icon: 'angle right' }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

SampleCharactersList.propTypes = {
  characters: PropTypes.array,
  loading: PropTypes.bool,
  onSelectCharacter: PropTypes.func.isRequired,
  onDeleteCharacter: PropTypes.func.isRequired,
  onNewCharacter: PropTypes.func.isRequired,
  selectedCharacter: PropTypes.object
};

export default SampleCharactersList;
